// ==========================================================================
// screens/Us.tsx — date nights, gift lists, notes (FF-052, 10 Oct 2026)
//   Caitlin's ideas, via James: "a date night list we can both add to and see/
//   plan in and add to calendar, A Gift list … your wish list … plus a list you
//   make for others … they cant see the list you make on them". Notes "both
//   shared and personal". The database hides what should be hidden (029).
// ==========================================================================
import { useMemo, useState, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  CalendarHeart, Check, Dices, ExternalLink, Gift, Heart, Home as HomeIcon, Lock, MapPin, NotebookPen, Pencil, Pin, Plane, Plus, Sparkles, Star, Trash2, Users, X,
} from "lucide-react";
import ChapterHeader from "@/components/ChapterHeader";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import { usePeople, useMe } from "@/lib/household";
import { useContacts } from "@/lib/contacts";
import { tokenColour, useEvents, useSaveEvent, ymd } from "@/lib/calendar";
import { money, toCents, fromCents } from "@/lib/money";
import {
  COST_LABEL, PRIORITY, SETTING_LABEL, useClaim, useDateIdeas, useGiftDone, useGifts, useNotes, useSaveDate, useSaveGift, useSaveNote, useUpdateDate,
  type Claim, type DateDraft, type DateIdea, type Gift as GiftT, type GiftDraft, type Note, type NoteDraft, type Setting,
} from "@/lib/us";

const pill = ({ isActive }: { isActive: boolean }) => `rounded-full px-4 py-2 text-sm transition ${isActive ? "bg-ink text-bg font-semibold" : "text-ink-muted hover:text-ink"}`;
const btn = "inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition disabled:opacity-50";
const primaryBtn = `${btn} bg-primary text-primary-ink hover:opacity-90`;
const quietBtn = `${btn} bg-surface-raised text-ink hover:bg-line/60`;
const SETTING_ICON: Record<Setting, typeof HomeIcon> = { home: HomeIcon, out: MapPin, away: Plane };

export default function Us() {
  return (
    <>
      <ChapterHeader chapter="us" title="Us" subtitle="Date nights, gift lists and notes. Some of it just for one of you." />
      <nav className="mb-7 flex flex-wrap gap-2">
        <NavLink to="/us/dates" className={pill}>Date nights</NavLink>
        <NavLink to="/us/gifts" className={pill}>Gifts</NavLink>
        <NavLink to="/us/notes" className={pill}>Notes</NavLink>
      </nav>
      <Routes>
        <Route path="dates" element={<DateNights />} />
        <Route path="gifts" element={<Gifts />} />
        <Route path="notes" element={<Notes />} />
        <Route path="*" element={<Navigate to="dates" replace />} />
      </Routes>
    </>
  );
}

function Band({ colour, icon: Icon, title, line, children }: { colour: string; icon: typeof Heart; title: string; line: ReactNode; children?: ReactNode }) {
  return (
    <div className="relative mb-8 overflow-hidden rounded-2xl px-5 py-6 text-white shadow-soft sm:px-8 sm:py-8"
      style={{ background: `linear-gradient(115deg, ${tokenColour(colour)} 0%, ${tokenColour(colour, 0.9)} 45%, ${tokenColour(colour, 0.72)} 100%)` }}>
      <Icon className="pointer-events-none absolute -right-6 -top-6 hidden h-48 w-48 opacity-[0.09] sm:block" strokeWidth={1.25} />
      <div className="relative flex flex-wrap items-end gap-x-8 gap-y-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-[32px] font-semibold leading-none sm:text-[44px]">{title}</h2>
          <p className="mt-3 max-w-xl text-[15px] text-white/85">{line}</p>
        </div>
        {children && <div className="flex w-full flex-wrap gap-3 sm:w-auto">{children}</div>}
      </div>
    </div>
  );
}

// ==========================================================================
// Date nights
// ==========================================================================
function Stars({ n, size = "h-4 w-4", onPick }: { n: number; size?: string; onPick?: (n: number) => void }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i)} aria-label={`${i} stars`} className={onPick ? "p-0.5" : "pointer-events-none"}>
          <Star className={`${size} ${i <= n ? "fill-chart-6 text-chart-6" : "text-line"}`} />
        </button>
      ))}
    </span>
  );
}

function DateNights() {
  const dates = useDateIdeas(); const events = useEvents();
  const [edit, setEdit] = useState<DateDraft | null>(null);
  const [plan, setPlan] = useState<DateIdea | null>(null);
  const [done, setDone] = useState<DateIdea | null>(null);
  const [picked, setPicked] = useState<DateIdea | null>(null);
  const [want, setWant] = useState<{ setting: Setting | "any"; cheap: boolean; noSitter: boolean }>({ setting: "any", cheap: false, noSitter: false });

  const all = dates.data ?? [];
  const evById = new Map((events.data ?? []).map((e) => [e.id, e]));
  const whenOf = (d: DateIdea) => { const e = d.event_id ? evById.get(d.event_id) : undefined; return e ? (e.starts_at ?? e.start_date) : null; };
  const today = ymd(new Date());
  const planned = all.filter((d) => d.event_id && !d.done_on && whenOf(d)).sort((a, b) => (whenOf(a) ?? "").localeCompare(whenOf(b) ?? ""));
  const upcoming = planned.filter((d) => (whenOf(d) ?? "").slice(0, 10) >= today);
  const howWasIt = planned.filter((d) => (whenOf(d) ?? "").slice(0, 10) < today);
  const ideas = all.filter((d) => !d.done_on && !(d.event_id && whenOf(d)));
  const memories = all.filter((d) => d.done_on).sort((a, b) => (b.done_on ?? "").localeCompare(a.done_on ?? ""));
  const pool = ideas.filter((d) => (want.setting === "any" || d.setting === want.setting) && (!want.cheap || d.cost <= 1) && (!want.noSitter || !d.needs_sitter));
  const pick = () => { if (!pool.length) { setPicked(null); return; } let p = pool[Math.floor(Math.random() * pool.length)]; if (pool.length > 1 && p.id === picked?.id) p = pool.find((x) => x.id !== picked.id)!; setPicked(p); };
  const avg = memories.filter((m) => m.rating).reduce((t, m, _, arr) => t + (m.rating ?? 0) / arr.length, 0);
  const next = upcoming[0];

  return (
    <>
      <Band colour="chart-1" icon={CalendarHeart} title="Date nights"
        line={next ? <>Next up: <b>{next.title}</b>, {fmtWhen(whenOf(next))}.</> : `${ideas.length} ${ideas.length === 1 ? "idea" : "ideas"} in the jar${memories.length ? `, ${memories.length} done` : ""}.`}>
        <button type="button" onClick={() => setEdit(blankDate())} className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-[15px] font-semibold text-chart-1 shadow-sm"><Plus className="h-4 w-4" />Add an idea</button>
      </Band>

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-9">
          {howWasIt.map((d) => (
            <button key={d.id} type="button" onClick={() => setDone(d)} className="flex w-full items-center gap-4 rounded-xl bg-chart-6/10 p-4 text-left ring-1 ring-chart-6/30" style={{ background: "rgb(var(--chart-6) / 0.1)" }}>
              <Star className="h-6 w-6 shrink-0 fill-chart-6 text-chart-6" />
              <span className="min-w-0 flex-1"><b className="block">How was {d.title}?</b><span className="text-sm text-ink-muted">{fmtWhen(whenOf(d))}. Give it some stars and a line to remember it by.</span></span>
            </button>
          ))}
          {upcoming.length > 0 && (
            <section>
              <h3 className="mb-3 text-[17px] font-semibold">Coming up</h3>
              <div className="space-y-3">
                {upcoming.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center gap-4 rounded-xl bg-surface p-4 shadow-soft ring-1 ring-line/70">
                    <DateBadge iso={whenOf(d)} />
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-lg font-semibold">{d.title}</div>
                      <div className="text-sm text-ink-muted">{[d.place, SETTING_LABEL[d.setting], d.needs_sitter ? "Babysitter needed" : null].filter(Boolean).join(", ")}</div>
                    </div>
                    <button type="button" onClick={() => setDone(d)} className={quietBtn}><Check className="h-4 w-4" />We did it</button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className="mb-3 text-[17px] font-semibold">Ideas</h3>
            {dates.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
            {!dates.isLoading && ideas.length === 0 && (
              <button type="button" onClick={() => setEdit(blankDate())} className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-line p-10 text-center text-ink-muted hover:text-ink">
                <CalendarHeart className="h-9 w-9" /><span className="font-semibold">Start the jar</span><span className="text-sm">A restaurant you've been meaning to try, a movie night, a day trip.</span>
              </button>
            )}
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]">
              {ideas.map((d) => <IdeaCard key={d.id} d={d} onOpen={() => setEdit({ ...d })} onPlan={() => setPlan(d)} />)}
            </div>
          </section>

          {memories.length > 0 && (
            <section>
              <h3 className="mb-3 flex items-baseline gap-3 text-[17px] font-semibold">Memories {avg > 0 && <span className="text-sm font-normal text-ink-muted">average {avg.toFixed(1)} stars</span>}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {memories.map((d) => (
                  <button key={d.id} type="button" onClick={() => setDone(d)} className="rounded-xl bg-surface p-4 text-left ring-1 ring-line/70 hover:shadow-soft">
                    <div className="flex items-start justify-between gap-2"><span className="font-heading text-lg font-semibold">{d.title}</span><Stars n={d.rating ?? 0} /></div>
                    <div className="text-xs text-ink-muted">{d.done_on && format(parseISO(d.done_on), "EEEE d MMMM yyyy")}</div>
                    {d.memory && <p className="mt-2 line-clamp-3 text-sm italic text-ink-muted">“{d.memory}”</p>}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* The jar */}
        <aside className="min-w-0">
          <div className="sticky top-6 rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
            <h3 className="flex items-center gap-2 text-[17px] font-semibold"><Dices className="h-5 w-5 text-chart-1" />Can't decide?</h3>
            <p className="mb-4 text-sm text-ink-muted">Pull one out of the jar.</p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {(["any", "home", "out", "away"] as const).map((s) => (
                <button key={s} type="button" onClick={() => setWant({ ...want, setting: s })} className={`rounded-full px-3 py-1.5 text-sm ${want.setting === s ? "bg-chart-1 font-semibold text-white" : "bg-surface-raised"}`}>{s === "any" ? "Anywhere" : SETTING_LABEL[s]}</button>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap gap-1.5">
              <button type="button" onClick={() => setWant({ ...want, cheap: !want.cheap })} className={`rounded-full px-3 py-1.5 text-sm ${want.cheap ? "bg-chart-1 font-semibold text-white" : "bg-surface-raised"}`}>Free or $</button>
              <button type="button" onClick={() => setWant({ ...want, noSitter: !want.noSitter })} className={`rounded-full px-3 py-1.5 text-sm ${want.noSitter ? "bg-chart-1 font-semibold text-white" : "bg-surface-raised"}`}>No babysitter</button>
            </div>
            <button type="button" onClick={pick} disabled={!pool.length} className="flex w-full items-center justify-center gap-2 rounded-full bg-chart-1 py-3 font-semibold text-white disabled:opacity-40"><Sparkles className="h-4 w-4" />{picked ? "Pick another" : "Pick one for us"}</button>
            {!pool.length && <p className="mt-2 text-center text-xs text-ink-muted">Nothing in the jar matches.</p>}
            {picked && (
              <div className="mt-5 rounded-xl bg-chart-1/10 p-4 text-center">
                <div className="font-heading text-2xl font-semibold text-ink">{picked.title}</div>
                <div className="mt-1 text-sm text-ink-muted">{[picked.place, SETTING_LABEL[picked.setting], COST_LABEL[picked.cost]].filter(Boolean).join(", ")}</div>
                <button type="button" onClick={() => setPlan(picked)} className={`${primaryBtn} mt-3`}><CalendarHeart className="h-4 w-4" />Plan it</button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {edit && <DateSheet d={edit} onClose={() => setEdit(null)} />}
      {plan && <PlanSheet d={plan} onClose={() => { setPlan(null); setPicked(null); }} />}
      {done && <DoneSheet d={done} when={whenOf(done)} onClose={() => setDone(null)} />}
    </>
  );
}

const fmtWhen = (iso: string | null) => (!iso ? "" : iso.length > 10 ? format(parseISO(iso), "EEE d MMM, h:mmaaa") : format(parseISO(iso), "EEE d MMM"));
function DateBadge({ iso }: { iso: string | null }) {
  if (!iso) return null;
  const d = parseISO(iso);
  return (
    <div className="flex w-14 shrink-0 flex-col items-center overflow-hidden rounded-lg ring-1 ring-chart-1/30">
      <span className="w-full bg-chart-1 py-0.5 text-center text-[11px] font-semibold uppercase text-white">{format(d, "MMM")}</span>
      <span className="font-heading text-2xl font-semibold leading-tight">{format(d, "d")}</span>
      <span className="pb-1 text-[11px] text-ink-muted">{iso.length > 10 ? format(d, "h:mmaaa") : format(d, "EEE")}</span>
    </div>
  );
}

function IdeaCard({ d, onOpen, onPlan }: { d: DateIdea; onOpen: () => void; onPlan: () => void }) {
  const I = SETTING_ICON[d.setting];
  return (
    <div className="flex min-w-0 flex-col rounded-xl bg-surface p-4 ring-1 ring-line/70 transition hover:shadow-soft">
      <button type="button" onClick={onOpen} className="min-w-0 text-left">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-chart-1"><I className="h-4 w-4" />{SETTING_LABEL[d.setting]}<span className="ml-auto text-ink-muted">{COST_LABEL[d.cost]}</span></div>
        <div className="font-heading text-lg font-semibold leading-snug">{d.title}</div>
        {d.place && <div className="mt-0.5 truncate text-sm text-ink-muted">{d.place}</div>}
        {d.note && <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{d.note}</p>}
      </button>
      <div className="mt-auto flex items-center gap-2 pt-4">
        {d.needs_sitter && <span className="inline-flex items-center gap-1 rounded-full bg-surface-raised px-2.5 py-1 text-xs"><Users className="h-3.5 w-3.5" />Sitter</span>}
        {d.link && <a href={d.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary"><ExternalLink className="h-3.5 w-3.5" />Link</a>}
        <button type="button" onClick={onPlan} className="ml-auto inline-flex items-center gap-1 rounded-full bg-chart-1/10 px-3 py-1.5 text-sm font-semibold text-chart-1 hover:bg-chart-1/20"><CalendarHeart className="h-4 w-4" />Plan it</button>
      </div>
    </div>
  );
}

const blankDate = (): DateDraft => ({ title: "", note: null, link: null, place: null, setting: "out", cost: 1, needs_sitter: false });

function DateSheet({ d, onClose }: { d: DateDraft; onClose: () => void }) {
  const save = useSaveDate(); const upd = useUpdateDate();
  const [f, setF] = useState(d); const [err, setErr] = useState<string | null>(null);
  const set = (p: Partial<DateDraft>) => setF({ ...f, ...p });
  const submit = () => {
    if (!f.title.trim()) { setErr("What's the idea?"); return; }
    if (f.link?.trim() && !/^https?:\/\//.test(f.link.trim())) { setErr("The link needs to start with http:// or https://"); return; }
    save.mutate(f, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={d.id ? "Date idea" : "Add a date idea"} onClose={onClose}>
      <div className="space-y-4">
        <Lbl label="The idea"><input className={inputCls} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Sunset picnic at Mt Coot-tha" autoFocus /></Lbl>
        <div>
          <span className="text-sm font-medium text-ink-muted">Where</span>
          <div className="mt-1.5 flex gap-2">
            {(["home", "out", "away"] as Setting[]).map((s) => { const I = SETTING_ICON[s]; return (
              <button key={s} type="button" onClick={() => set({ setting: s })} className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm ${f.setting === s ? "bg-chart-1 font-semibold text-white" : "bg-surface-raised"}`}><I className="h-4 w-4" />{SETTING_LABEL[s]}</button>
            ); })}
          </div>
        </div>
        <div>
          <span className="text-sm font-medium text-ink-muted">Cost</span>
          <div className="mt-1.5 flex gap-2">
            {COST_LABEL.map((c, i) => <button key={c} type="button" onClick={() => set({ cost: i as 0 | 1 | 2 | 3 })} className={`flex-1 rounded-xl py-2.5 text-sm ${f.cost === i ? "bg-chart-1 font-semibold text-white" : "bg-surface-raised"}`}>{c}</button>)}
          </div>
        </div>
        <label className="flex items-center gap-3 rounded-xl bg-surface-raised p-3 text-sm"><input type="checkbox" checked={f.needs_sitter} onChange={(e) => set({ needs_sitter: e.target.checked })} className="h-4 w-4 accent-[rgb(var(--chart-1))]" />Needs a babysitter</label>
        <Lbl label="Place (optional)"><input className={inputCls} value={f.place ?? ""} onChange={(e) => set({ place: e.target.value })} placeholder="Restaurant or address" /></Lbl>
        <Lbl label="Link (optional)"><input className={inputCls} value={f.link ?? ""} onChange={(e) => set({ link: e.target.value })} inputMode="url" placeholder="https://" /></Lbl>
        <Lbl label="Note (optional)"><textarea className={`${inputCls} min-h-[70px]`} value={f.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="Book ahead, ask for the window table…" /></Lbl>
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className={`${primaryBtn} flex-1 py-3`}>{d.id ? "Save" : "Add to the jar"}</button>
          {d.id && <button type="button" onClick={() => upd.mutate({ id: d.id!, patch: { archive: true } }, { onSuccess: onClose })} className={`${quietBtn} py-3 text-bad`}><Trash2 className="h-4 w-4" />Remove</button>}
        </div>
      </div>
    </Sheet>
  );
}

function PlanSheet({ d, onClose }: { d: DateIdea; onClose: () => void }) {
  const people = usePeople(); const saveEvent = useSaveEvent(); const upd = useUpdateDate();
  const adults = (people.data ?? []).filter((p) => p.role === "adult").map((p) => p.id);
  const [date, setDate] = useState(ymd(new Date())); const [time, setTime] = useState(d.setting === "home" ? "19:30" : "18:30");
  const [allDay, setAllDay] = useState(d.setting === "away"); const [err, setErr] = useState<string | null>(null);
  const submit = () => {
    saveEvent.mutate({ d: { title: `Date night: ${d.title}`, notes: d.note ?? "", location: d.place ?? "", all_day: allDay, date, startTime: time, endTime: "", repeat: "none", repeat_until: "", endDate: "", people: adults } }, {
      onSuccess: (id) => upd.mutate({ id: d.id, patch: { event_id: id } }, { onSuccess: onClose, onError: (e) => setErr(e.message) }),
      onError: (e) => setErr(e.message),
    });
  };
  return (
    <Sheet title="Plan it" onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded-xl bg-chart-1/10 p-4"><div className="font-heading text-xl font-semibold">{d.title}</div>{d.place && <div className="text-sm text-ink-muted">{d.place}</div>}</div>
        <Lbl label="Day"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Lbl>
        <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-4 w-4" />All day (a day trip or a weekend away)</label>
        {!allDay && <Lbl label="Time"><input type="time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} /></Lbl>}
        {d.needs_sitter && <p className="rounded-lg bg-warn/10 p-3 text-sm text-warn">Needs a babysitter. Worth adding a task to line one up.</p>}
        <p className="text-sm text-ink-muted">It goes on the family calendar for both of you as "Date night: {d.title}".</p>
        <ErrorText>{err}</ErrorText>
        <button type="button" onClick={submit} disabled={saveEvent.isPending || upd.isPending} className={`${primaryBtn} w-full py-3`}><CalendarHeart className="h-4 w-4" />Put it on the calendar</button>
      </div>
    </Sheet>
  );
}

function DoneSheet({ d, when, onClose }: { d: DateIdea; when: string | null; onClose: () => void }) {
  const upd = useUpdateDate(); const save = useSaveDate();
  const [rating, setRating] = useState(d.rating ?? 0); const [memory, setMemory] = useState(d.memory ?? "");
  const [on, setOn] = useState(d.done_on ?? (when ? when.slice(0, 10) : ymd(new Date())));
  const again = () => save.mutate({ title: d.title, note: d.note, link: d.link, place: d.place, setting: d.setting, cost: d.cost, needs_sitter: d.needs_sitter }, { onSuccess: onClose });
  return (
    <Sheet title={d.title} onClose={onClose}>
      <div className="space-y-5">
        <div><span className="text-sm font-medium text-ink-muted">How was it?</span><div className="mt-1"><Stars n={rating} size="h-8 w-8" onPick={setRating} /></div></div>
        <Lbl label="Day"><input type="date" className={inputCls} value={on} onChange={(e) => setOn(e.target.value)} /></Lbl>
        <Lbl label="Remember it by" hint="What you ate, what made you laugh, worth doing again?"><textarea className={`${inputCls} min-h-[110px]`} value={memory} onChange={(e) => setMemory(e.target.value)} /></Lbl>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => upd.mutate({ id: d.id, patch: { done_on: on, rating: rating || null, memory: memory.trim() || null } }, { onSuccess: onClose })} className={`${primaryBtn} flex-1 py-3`}><Heart className="h-4 w-4" />Save the memory</button>
          {d.done_on && <button type="button" onClick={again} className={`${quietBtn} py-3`}><Plus className="h-4 w-4" />Back in the jar</button>}
        </div>
      </div>
    </Sheet>
  );
}

// ==========================================================================
// Gifts
// ==========================================================================
function Gifts() {
  const gifts = useGifts(); const people = usePeople(); const me = useMe(); const contacts = useContacts();
  const family = people.data ?? [];
  const [who, setWho] = useState<string | null>(null);
  const [edit, setEdit] = useState<GiftDraft | null>(null);
  const viewing = who ?? me?.id ?? null;
  const person = family.find((p) => p.id === viewing);
  const mine = viewing === me?.id;
  const all = gifts.data?.gifts ?? []; const claims = gifts.data?.claims ?? [];
  const wishes = all.filter((g) => g.kind === "wish" && g.for_person_id === viewing);
  const myIdeas = all.filter((g) => g.kind === "idea");
  const nameOf = (g: GiftT) => g.for_person_id ? family.find((p) => p.id === g.for_person_id)?.display_name ?? "Someone" : (contacts.data ?? []).find((c) => c.id === g.for_contact_id)?.name ?? "Someone";
  const byRecipient = useMemo(() => {
    const m = new Map<string, GiftT[]>();
    for (const g of myIdeas) { const k = (g.for_person_id ?? g.for_contact_id)!; m.set(k, [...(m.get(k) ?? []), g]); }
    return [...m.entries()].sort((a, b) => nameOf(a[1][0]).localeCompare(nameOf(b[1][0])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myIdeas.length, family.length, contacts.data]);

  return (
    <>
      <Band colour="chart-5" icon={Gift} title="Gifts"
        line="Your wish list is for everyone to see. Your ideas for other people stay with you." />

      <div className="grid gap-8 xl:grid-cols-2">
        {/* Wish lists */}
        <section className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <h3 className="mr-2 text-[17px] font-semibold">Wish lists</h3>
            {family.map((p) => (
              <button key={p.id} type="button" onClick={() => setWho(p.id)}
                className={`rounded-full px-3.5 py-1.5 text-sm transition ${viewing === p.id ? "font-semibold text-white" : "bg-surface-raised text-ink"}`}
                style={viewing === p.id ? { background: tokenColour(p.colour) } : undefined}>{p.id === me?.id ? "Mine" : p.display_name}</button>
            ))}
          </div>
          <div className="rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-heading text-2xl font-semibold" style={{ color: person ? tokenColour(person.colour) : undefined }}>{mine ? "My wish list" : `${person?.display_name ?? ""}'s wish list`}</div>
                <div className="text-sm text-ink-muted">{mine ? "Everyone in the family can see this." : person?.role === "child" ? "Ideas for her, kept by you two." : "Tap “I've got this” so nobody doubles up. They won't see it."}</div>
              </div>
              {(mine || person?.role === "child") && viewing && (
                <button type="button" onClick={() => setEdit(blankGift("wish", viewing))} className={primaryBtn}><Plus className="h-4 w-4" />Add</button>
              )}
            </div>
            {wishes.length === 0 && <p className="py-8 text-center text-sm text-ink-muted">{mine ? "Nothing on it yet. Add the things you'd love." : "Nothing on this list yet."}</p>}
            <ul className="space-y-2">
              {wishes.map((g) => <WishRow key={g.id} g={g} claim={claims.find((c) => c.gift_id === g.id)} mine={mine} meId={me?.id} people={family} editable={g.created_by === me?.id || person?.role === "child"} onEdit={() => setEdit({ ...g })} />)}
            </ul>
          </div>
        </section>

        {/* My ideas */}
        <section className="min-w-0">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-[17px] font-semibold"><Lock className="h-4 w-4 text-ink-muted" />My gift ideas</h3>
            <button type="button" onClick={() => setEdit(blankGift("idea", family.find((p) => p.id !== me?.id)?.id ?? ""))} className={primaryBtn}><Plus className="h-4 w-4" />Add an idea</button>
          </div>
          {byRecipient.length === 0 && (
            <div className="rounded-2xl border-2 border-dashed border-line p-10 text-center text-sm text-ink-muted">
              <Gift className="mx-auto mb-2 h-8 w-8" />Jot ideas down when you think of them: for Caitlin, for Isadora, for your mum's birthday. Only you see them unless you share one.
            </div>
          )}
          <div className="space-y-4">
            {byRecipient.map(([k, list]) => (
              <div key={k} className="rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
                <div className="mb-3 flex items-center gap-2 font-semibold">{nameOf(list[0])}<span className="text-sm font-normal text-ink-muted">{(() => { const n = list.filter((g) => !g.given_on).length; return n === 1 ? "1 idea" : `${n} ideas`; })()}</span></div>
                <ul className="space-y-2">{list.map((g) => <IdeaRow key={g.id} g={g} meId={me?.id} onEdit={() => setEdit({ ...g })} />)}</ul>
              </div>
            ))}
          </div>
        </section>
      </div>

      {edit && <GiftSheet d={edit} meId={me?.id} onClose={() => setEdit(null)} />}
    </>
  );
}

const blankGift = (kind: "wish" | "idea", forPerson: string): GiftDraft => ({ kind, for_person_id: forPerson || null, for_contact_id: null, title: "", link: null, price_cents: null, priority: 2, note: null, occasion: null, shared: false });

function WishRow({ g, claim, mine, meId, people, editable, onEdit }: { g: GiftT; claim?: Claim; mine: boolean; meId?: string; people: { id: string; display_name: string }[]; editable: boolean; onEdit: () => void }) {
  const toggle = useClaim();
  const by = claim ? people.find((p) => p.id === claim.claimed_by)?.display_name : null;
  const canEdit = editable;
  return (
    <li className={`flex items-center gap-3 rounded-xl p-3 ring-1 ring-line/70 ${claim ? "bg-surface-raised/60" : ""}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-chart-5/12 text-chart-5" style={{ background: "rgb(var(--chart-5) / 0.12)" }}>
        {g.priority === 1 ? <Heart className="h-4 w-4 fill-current" /> : <Gift className="h-4 w-4" />}
      </span>
      <button type="button" onClick={canEdit ? onEdit : undefined} className={`min-w-0 flex-1 text-left ${canEdit ? "" : "cursor-default"}`}>
        <span className={`block truncate font-semibold ${claim ? "text-ink-muted line-through decoration-1" : ""}`}>{g.title}</span>
        <span className="block truncate text-xs text-ink-muted">{[PRIORITY[g.priority], g.price_cents ? money(g.price_cents) : null, g.note].filter(Boolean).join(", ")}</span>
      </button>
      {g.link && <a href={g.link} target="_blank" rel="noreferrer" aria-label="Open link" className="shrink-0 rounded-full p-2 text-primary hover:bg-surface-raised"><ExternalLink className="h-4 w-4" /></a>}
      {!mine && (claim
        ? <button type="button" onClick={() => toggle.mutate({ giftId: g.id, claim })} title="Tap to free it up again" className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${claim.claimed_by === meId ? "bg-good/15 text-good" : "bg-surface-raised text-ink-muted"}`}>
            {claim.claimed_by === meId ? "You've got it" : `${by ?? "Someone"}'s got it`}
          </button>
        : <button type="button" onClick={() => toggle.mutate({ giftId: g.id })} disabled={toggle.isPending} className="shrink-0 rounded-full bg-chart-5 px-3 py-1.5 text-xs font-semibold text-white">I've got this</button>)}
    </li>
  );
}

function IdeaRow({ g, meId, onEdit }: { g: GiftT; meId?: string; onEdit: () => void }) {
  const done = useGiftDone();
  return (
    <li className={`flex items-center gap-3 rounded-xl p-3 ring-1 ring-line/70 ${g.given_on ? "opacity-55" : ""}`}>
      <button type="button" onClick={() => done.mutate({ id: g.id, given: !g.given_on })} aria-label={g.given_on ? "Not given yet" : "Given"}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${g.given_on ? "border-good bg-good text-white" : "border-line hover:border-chart-5"}`}>{g.given_on && <Check className="h-4 w-4" />}</button>
      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <span className={`block truncate font-semibold ${g.given_on ? "line-through" : ""}`}>{g.title}</span>
        <span className="block truncate text-xs text-ink-muted">{[g.occasion, g.price_cents ? money(g.price_cents) : null, g.shared ? (g.created_by === meId ? "shared" : "shared with you") : null].filter(Boolean).join(", ")}</span>
      </button>
      {g.shared ? <Users className="h-4 w-4 shrink-0 text-ink-muted" /> : <Lock className="h-4 w-4 shrink-0 text-ink-muted" />}
      {g.link && <a href={g.link} target="_blank" rel="noreferrer" aria-label="Open link" className="shrink-0 rounded-full p-2 text-primary hover:bg-surface-raised"><ExternalLink className="h-4 w-4" /></a>}
    </li>
  );
}

function GiftSheet({ d, meId, onClose }: { d: GiftDraft; meId?: string; onClose: () => void }) {
  const save = useSaveGift(); const done = useGiftDone(); const people = usePeople(); const contacts = useContacts();
  const [f, setF] = useState(d); const [price, setPrice] = useState(fromCents(d.price_cents)); const [err, setErr] = useState<string | null>(null);
  const set = (p: Partial<GiftDraft>) => setF({ ...f, ...p });
  const idea = f.kind === "idea";
  const others = (people.data ?? []).filter((p) => p.id !== meId);
  const friends = (contacts.data ?? []).filter((c) => !c.archived_at && ["friend", "family", "person"].includes(c.kind));
  const recipient = f.for_person_id ? `p:${f.for_person_id}` : f.for_contact_id ? `c:${f.for_contact_id}` : "";
  const forFamily = !!f.for_person_id;
  const submit = () => {
    if (!f.title.trim()) { setErr("What's the gift?"); return; }
    if (f.link?.trim() && !/^https?:\/\//.test(f.link.trim())) { setErr("The link needs to start with http:// or https://"); return; }
    if (!f.for_person_id && !f.for_contact_id) { setErr("Who's it for?"); return; }
    save.mutate({ ...f, price_cents: price.trim() ? toCents(price) : null, shared: idea && f.shared }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={d.id ? (idea ? "Gift idea" : "Wish") : idea ? "New gift idea" : "Add to the wish list"} onClose={onClose}>
      <div className="space-y-4">
        {idea && (
          <Lbl label="Who's it for">
            <select className={inputCls} value={recipient} onChange={(e) => { const [t, id] = e.target.value.split(":"); set({ for_person_id: t === "p" ? id : null, for_contact_id: t === "c" ? id : null }); }}>
              <option value="">Pick someone</option>
              <optgroup label="Family">{others.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.display_name}</option>)}</optgroup>
              {friends.length > 0 && <optgroup label="Contacts">{friends.map((c) => <option key={c.id} value={`c:${c.id}`}>{c.name}</option>)}</optgroup>}
            </select>
          </Lbl>
        )}
        <Lbl label={idea ? "The idea" : "What you'd love"}><input className={inputCls} value={f.title} onChange={(e) => set({ title: e.target.value })} autoFocus /></Lbl>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Price (about)"><input className={inputCls} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$" /></Lbl>
          {idea
            ? <Lbl label="Occasion"><input className={inputCls} value={f.occasion ?? ""} onChange={(e) => set({ occasion: e.target.value })} placeholder="Birthday, Christmas" /></Lbl>
            : <Lbl label="How much"><select className={inputCls} value={f.priority} onChange={(e) => set({ priority: Number(e.target.value) as 1 | 2 | 3 })}>{([1, 2, 3] as const).map((p) => <option key={p} value={p}>{PRIORITY[p]}</option>)}</select></Lbl>}
        </div>
        <Lbl label="Link (optional)"><input className={inputCls} value={f.link ?? ""} onChange={(e) => set({ link: e.target.value })} inputMode="url" placeholder="https://" /></Lbl>
        <Lbl label="Note (optional)"><input className={inputCls} value={f.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder={idea ? "Size, colour, where to buy" : "Size 10, the blue one"} /></Lbl>
        {idea && (
          <label className="flex items-start gap-3 rounded-xl bg-surface-raised p-3 text-sm">
            <input type="checkbox" checked={f.shared} onChange={(e) => set({ shared: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span><b className="block">Share with the family</b><span className="text-ink-muted">{forFamily ? "Everyone except the person it's for can see and tick it. Good for Isadora's presents." : "The other adults can see it too."}</span></span>
          </label>
        )}
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className={`${primaryBtn} flex-1 py-3`}>Save</button>
          {d.id && <button type="button" onClick={() => done.mutate({ id: d.id!, archive: true }, { onSuccess: onClose })} className={`${quietBtn} py-3 text-bad`}><Trash2 className="h-4 w-4" />Remove</button>}
        </div>
      </div>
    </Sheet>
  );
}

// ==========================================================================
// Notes
// ==========================================================================
const NOTE_COLOURS = ["chart-6", "chart-1", "chart-3", "chart-4", "chart-5", "chart-2"] as const;

function Notes() {
  const notes = useNotes(); const save = useSaveNote(); const me = useMe(); const people = usePeople();
  const [show, setShow] = useState<"all" | "shared" | "mine">("all");
  const [text, setText] = useState(""); const [priv, setPriv] = useState(false);
  const [edit, setEdit] = useState<(NoteDraft & { created_by?: string }) | null>(null);
  const list = (notes.data ?? []).filter((n) => show === "all" || (show === "shared" ? !n.private : n.private));
  const add = () => {
    const t = text.trim(); if (!t) return;
    const [first, ...rest] = t.split("\n");
    save.mutate(rest.length ? { title: first, body: rest.join("\n").trim() || first, private: priv, pinned: false, colour: null } : { title: null, body: t, private: priv, pinned: false, colour: null }, { onSuccess: () => setText("") });
  };
  return (
    <>
      <div className="mb-6 rounded-2xl bg-surface p-4 shadow-soft ring-1 ring-line/70">
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Jot something down. The first line becomes the title." className="min-h-[80px] w-full resize-y bg-transparent text-[15px] outline-none placeholder:text-ink-muted"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setPriv(!priv)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${priv ? "bg-ink text-bg font-semibold" : "bg-surface-raised"}`}>{priv ? <Lock className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}{priv ? "Just me" : "Shared"}</button>
          <button type="button" onClick={add} disabled={!text.trim() || save.isPending} className={`${primaryBtn} ml-auto`}><Plus className="h-4 w-4" />Add note</button>
        </div>
      </div>
      <div className="mb-4 flex gap-2">
        {([["all", "All"], ["shared", "Shared"], ["mine", "Just me"]] as const).map(([k, l]) => <button key={k} type="button" onClick={() => setShow(k)} className={pill({ isActive: show === k })}>{l}</button>)}
      </div>
      {notes.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
      {!notes.isLoading && list.length === 0 && <div className="rounded-2xl border-2 border-dashed border-line p-10 text-center text-sm text-ink-muted"><NotebookPen className="mx-auto mb-2 h-8 w-8" />No notes here yet.</div>}
      <div className="columns-1 gap-4 sm:columns-2 xl:columns-3 [&>*]:mb-4">
        {list.map((n) => <NoteCard key={n.id} n={n} who={people.data?.find((p) => p.id === n.created_by)?.display_name} onOpen={() => setEdit({ ...n })} />)}
      </div>
      {edit && <NoteSheet d={edit} canEdit={!edit.private || edit.created_by === me?.id} onClose={() => setEdit(null)} />}
    </>
  );
}

function NoteCard({ n, who, onOpen }: { n: Note; who?: string; onOpen: () => void }) {
  const c = n.colour ?? "chart-6";
  return (
    <button type="button" onClick={onOpen} className="block w-full break-inside-avoid rounded-2xl p-5 text-left ring-1 transition hover:shadow-soft"
      style={{ background: tokenColour(c, 0.1), borderColor: tokenColour(c, 0.3), boxShadow: `inset 0 3px 0 ${tokenColour(c)}` }}>
      <div className="mb-1 flex items-start gap-2">
        {n.title && <span className="min-w-0 flex-1 font-semibold">{n.title}</span>}
        {n.pinned && <Pin className="ml-auto h-4 w-4 shrink-0 fill-current text-ink-muted" />}
        {n.private && <Lock className="h-4 w-4 shrink-0 text-ink-muted" />}
      </div>
      <p className="line-clamp-[10] whitespace-pre-line text-[15px] text-ink">{n.body}</p>
      <div className="mt-3 text-xs text-ink-muted">{who}{who && ", "}{format(parseISO(n.updated_at), "d MMM")}</div>
    </button>
  );
}

function NoteSheet({ d, canEdit, onClose }: { d: NoteDraft; canEdit: boolean; onClose: () => void }) {
  const save = useSaveNote();
  const [f, setF] = useState(d); const set = (p: Partial<NoteDraft>) => setF({ ...f, ...p });
  return (
    <Sheet title={d.id ? "Note" : "New note"} onClose={onClose}>
      <div className="space-y-4">
        <input className={`${inputCls} font-semibold`} value={f.title ?? ""} onChange={(e) => set({ title: e.target.value })} placeholder="Title (optional)" disabled={!canEdit} />
        <textarea className={`${inputCls} min-h-[260px]`} value={f.body} onChange={(e) => set({ body: e.target.value })} disabled={!canEdit} />
        <div className="flex flex-wrap items-center gap-2">
          {NOTE_COLOURS.map((c) => <button key={c} type="button" aria-label={c} onClick={() => set({ colour: c })} className={`h-8 w-8 rounded-full ${(f.colour ?? "chart-6") === c ? "ring-2 ring-ink ring-offset-2 ring-offset-surface" : ""}`} style={{ background: tokenColour(c) }} />)}
          <button type="button" onClick={() => set({ pinned: !f.pinned })} className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${f.pinned ? "bg-ink text-bg font-semibold" : "bg-surface-raised"}`}><Pin className="h-3.5 w-3.5" />{f.pinned ? "Pinned" : "Pin"}</button>
          <button type="button" onClick={() => set({ private: !f.private })} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${f.private ? "bg-ink text-bg font-semibold" : "bg-surface-raised"}`}>{f.private ? <Lock className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}{f.private ? "Just me" : "Shared"}</button>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => save.mutate(f, { onSuccess: onClose })} disabled={!f.body.trim() || save.isPending} className={`${primaryBtn} flex-1 py-3`}><Pencil className="h-4 w-4" />Save</button>
          {d.id && <button type="button" onClick={() => save.mutate({ ...f, archive: true }, { onSuccess: onClose })} className={`${quietBtn} py-3 text-bad`}><Trash2 className="h-4 w-4" />Remove</button>}
          <button type="button" onClick={onClose} className={`${quietBtn} py-3`} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
      </div>
    </Sheet>
  );
}
